import * as React from 'react'
import { Form, Input, Button, Spin, Dropdown, Menu, Tabs, message } from 'antd'
import { set, cloneDeep, debounce, isEqual, get } from 'lodash'
import produce from 'immer'
import { showError, resourceNotFound, ValidationError, showClientNotifications } from 'helpers/errors'
import {
  createLabelFactory,
  createFieldFactory,
  createHandleSubmit,
  createDropdownRender,
  createSaveDocumentItems,
  isReadOnly,
} from 'helpers/formViews'
import { DEBOUNCE } from 'helpers/utils'
import { formatUserTime, formatDateTime } from 'helpers/dateTime'
import { getUserPermission } from 'helpers/auth'
import { t } from 'helpers/i18n'
import { Emitter } from 'helpers/events'
import { FORM_CHANGE_EVENT } from 'options/events'
import Select, { Option, getOptionProps } from 'elements/Select'
import Icon from 'elements/Icon'
import Modal from 'elements/Modal'
import { Row, Col } from 'elements/Grid'
import Checkbox from 'elements/Checkbox'
import Documents from 'containers/Documents/ChildListView'
import FormButtons from 'elements/FormButtons'

class FormView extends React.Component {
  state = { jobTemplateId: 0 }

  constructor(props) {
    super(props)

    this.handleSubmit = createHandleSubmit(this)
    this.searchAssets = debounce(this.searchAssets, DEBOUNCE)
    this.saveDocumentItems = createSaveDocumentItems(this, 'JobRequest')
  }

  componentDidMount() {
    this.fetchItem()

    Emitter.on(FORM_CHANGE_EVENT, this.handleFormChange)
  }

  componentWillUnmount() {
    Emitter.off(FORM_CHANGE_EVENT, this.handleFormChange)
  }

  handleFormChange = () => this.setState({ formHasChanged: true })

  getItemId = () => [this.state.item?.id, this.props.linkTargetRecord?.jobRequestId].find(Boolean)

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const responses = await Promise.all([
        itemId
          ? this.props.getItem(itemId).catch((error) => {
              this.props.onCancel?.()
              throw error
            })
          : !this.props.linkTargetRecord
            ? this.props.newItem({})
            : resourceNotFound(this),
        this.props.getSettings({ type: this.props.settingsType }),
        this.props.getAssetCategories(),
        this.props.getLocations(),
      ])

      const item = cloneDeep(responses[0].value.data)

      item.locationId = item.locationId || undefined
      item.locationName = item.locationId ? item.locationName : ''

      const [assets, documentItems, templates] = await Promise.all([
        this.props.getAssets({
          locationIds: item.locationId ? [item.locationId] : undefined,
          assetIds: item.assetId ? [item.assetId] : undefined,
          assetCategoryIds: item.assetCategoryId ? [item.assetCategoryId] : undefined,
        }),
        item.id
          ? this.props.getDocumentItems({
              request: {
                domainObjectId: item.id,
                domainObjectType: 'JobRequest',
                documentType: 'ObjectDocument',
              },
            })
          : Promise.resolve(null),
        item.status === 'Open' ? this.props.getTemplates() : Promise.resolve(null),
      ])

      this.setState({
        item,
        itemOriginal: cloneDeep(item),
        documentItems: documentItems?.value?.data?.items ?? [],
        documentItemsOriginal: documentItems?.value?.data?.items ?? [],
        documentItemsTotals: documentItems?.value?.data?.pageTotals ?? {},
        fieldSettings: responses[1].value.data.fieldSettings,
        assetCategories: get(responses[2], 'value.data.items', []),
        locations: get(responses[3], 'value.data.items', []),
        templates: get(templates, 'value.data.items', []),
        assets: assets?.value?.data ?? {},
        createJobVisible: false,
        createJobLoading: false,
        declineVisible: false,
        declineLoading: false,
        jobTemplateId: 0,
        comment: '',
        formHasChanged: false,
      })
    } catch (error) {
      showError({ error })
    }
  }

  searchAssets = async (search) => {
    try {
      const { assetId, locationId, assetCategoryId } = this.state.item ?? {}
      const response = await this.props.getAssets({
        search,
        assetIds: assetId ? [assetId] : undefined,
        locationIds: locationId ? [locationId] : undefined,
        assetCategoryIds: assetCategoryId ? [assetCategoryId] : undefined,
      })

      this.setState({ assets: response.value.data })
    } catch (error) {
      showError({ error })
    }
  }

  saveItem = async (item) => {
    if (!item) {
      throw new Error('item is undefined')
    }

    const saved = item.id ? await this.props.updateItem(item) : await this.props.createItem(item)

    await this.saveDocumentItems(saved.value.data.id)

    return saved
  }

  saveItemSuccess = async (savedItem, saveItemParams) => {
    if (!saveItemParams.id) {
      const response = await this.props.sendCreateNotification({ id: savedItem.id })

      showClientNotifications({ response })

      return response
    }

    return Promise.resolve(true)
  }

  handleActionsMenuClick = ({ key }) => {
    if (this.hasUnsavedChanges()) {
      message.error(t('saveChangesFirst'))
      return
    }

    if (key === 'decline') {
      this.setState({ declineVisible: true })
    }

    if (key === 'createJob') {
      this.setState({ createJobVisible: true })
    }
  }

  handleCreateJobOk = async () => {
    try {
      const { item, comment, jobTemplateId } = this.state

      await this.props.onSave?.(true)

      this.setState({ createJobLoading: true })

      const response = await this.props.createJob({
        id: item.id,
        comment,
        jobTemplateId,
      })

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }

      if (this.state.addJobRequestDocumentsToJob) {
        const copied = await this.props.copyAllObjectDocuments({
          sourceRequest: {
            domainObjectId: this.state.item.id,
            domainObjectType: 'JobRequest',
          },
          destinationRequest: {
            domainObjectId: response.value.data.items[0].id,
            domainObjectType: 'Job',
          },
        })

        showClientNotifications({ response: copied })

        if (copied.value.data.failureCount > 0) {
          throw new Error()
        }
      }

      await this.fetchItem()
      await this.props.onSave?.(false)
    } catch (error) {
      showError({ error })
      await this.props.onSave?.(false)
    } finally {
      this.setState({ createJobLoading: false })
    }
  }

  handleDeclineOk = async () => {
    try {
      const { item, comment } = this.state

      await this.props.onSave?.(true)

      this.setState({ declineLoading: true })

      const response = await this.props.decline({
        id: item.id,
        comment,
      })

      showClientNotifications({ response })

      await this.fetchItem()

      await this.props.onSave?.(false)
    } catch (error) {
      showError({ error })
      await this.props.onSave?.(false)
    } finally {
      this.setState({ declineLoading: false })
    }
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        this.props.form.setFieldsValue({ [name]: value })
      }),
      async () => {
        if (['locationId', 'assetCategoryId'].includes(name)) {
          try {
            const { locationId, assetCategoryId } = this.state.item ?? {}

            this.setItemValue('assetId', 0)

            const response = await this.props.getAssets({
              locationIds: locationId ? [locationId] : [],
              assetCategoryIds: assetCategoryId ? [assetCategoryId] : [],
            })

            this.setState({ assets: response.value.data })
          } catch (error) {
            showError({ error })
          }
        }
      }
    )

  hasUnsavedChanges = () =>
    this.state.formHasChanged ||
    !this.state.item?.id ||
    !isEqual(this.state.item, this.state.itemOriginal) ||
    !isEqual(this.state.documentItems, this.state.documentItemsOriginal)

  render() {
    const { item, comment, jobTemplateId, addJobRequestDocumentsToJob, fieldSettings, assets } = this.state

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)

    const readOnly = isReadOnly(this) || ['Closed', 'Declined'].includes(item.status)

    return (
      <>
        <Form
          layout="vertical"
          onSubmit={this.handleSubmit}
          className="tofino-tickets-form-view"
          colon={false}
        >
          {item.id &&
            item.status !== 'Closed' &&
            getUserPermission('AllowCreateJobRequestOnly') !== 'Yes' && (
              <Dropdown
                overlay={
                  <Menu onClick={this.handleActionsMenuClick}>
                    <Menu.Item key="createJob">{t('createJob')}</Menu.Item>
                    {item.status === 'Open' && <Menu.Item key="decline">{t('decline')}</Menu.Item>}
                  </Menu>
                }
                trigger={['click']}
              >
                <Button className="form-items-actions">
                  {t('actions')}
                  <Icon type="KeyboardArrowDown" />
                </Button>
              </Dropdown>
            )}
          <div className="form-items-container">
            <ValidationError errors={this.state.validationErrors} />
            <Row>
              <Col md={16} xs={24}>
                <Form.Item label={createLabel('title')}>
                  {createFieldDecorator('title')(
                    <Input
                      onChange={(e) => this.setItemValue('title', e.target.value)}
                      disabled={item.status !== 'Open'}
                      autoFocus
                    />
                  )}
                </Form.Item>
              </Col>
              <Col md={8} xs={24}>
                <Form.Item label={createLabel('id')}>
                  <Input value={item.id} disabled />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col md={16} xs={24}>
                <Form.Item label={createLabel('locationId')}>
                  {createFieldDecorator('locationId')(
                    <Select
                      onChange={(value) => this.setItemValue('locationId', value)}
                      disabled={item.status !== 'Open'}
                    >
                      {(this.state.locations ?? [])
                        .filter((each) => each.id)
                        .map((each) => (
                          <Option key={each.id} value={each.id}>
                            <span {...getOptionProps(each)}>{each.displayName}</span>
                          </Option>
                        ))}
                    </Select>
                  )}
                </Form.Item>
              </Col>
              <Col md={8} xs={24}>
                <Form.Item label={createLabel('createdDate')}>
                  <Input value={formatDateTime(item.createdDate, { showTime: true })} disabled />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col md={16} xs={24}>
                <Form.Item label={createLabel('assetCategoryId')}>
                  {createFieldDecorator('assetCategoryId')(
                    <Select
                      onChange={(value) => this.setItemValue('assetCategoryId', value || 0)}
                      disabled={readOnly}
                    >
                      <Option value={0}>{t('all')}</Option>
                      {(this.state.assetCategories ?? []).map((each) => (
                        <Option key={each.id} value={each.id}>
                          <span {...getOptionProps(each)}>{each.displayName}</span>
                        </Option>
                      ))}
                    </Select>
                  )}
                </Form.Item>
              </Col>
              <Col md={8} xs={24}>
                <Form.Item label={createLabel('status')}>
                  <Input value={item.statusName} disabled />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col md={16} xs={24}>
                <Form.Item label={createLabel('assetId')}>
                  {createFieldDecorator('assetId')(
                    <Select
                      dropdownRender={createDropdownRender(assets)}
                      onSearch={this.searchAssets}
                      disabled={item.status !== 'Open'}
                      allowClear={false}
                      showSearch
                    >
                      <Option value={0}>{t('none')}</Option>
                      {(assets?.items ?? [])
                        .filter((each) => each.id)
                        .map((each) => (
                          <Option key={each.id} value={each.id}>
                            <span {...getOptionProps(each)}>{each.displayName}</span>
                          </Option>
                        ))}
                    </Select>
                  )}
                </Form.Item>
              </Col>
              <Col md={8} xs={24}>
                <Form.Item label={createLabel('createdByName')}>
                  {createFieldDecorator('createdByName')(
                    <Input
                      onChange={(e) => this.setItemValue('createdByName', e.target.value)}
                      disabled={item.status !== 'Open'}
                    />
                  )}
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col md={16} xs={24}>
                <Form.Item label={createLabel('description')}>
                  {createFieldDecorator('description')(
                    <Input.TextArea
                      rows={5}
                      onChange={(e) => this.setItemValue('description', e.target.value)}
                      disabled={item.status !== 'Open'}
                    />
                  )}
                </Form.Item>
              </Col>
              <Col md={8} xs={24}>
                <Form.Item label={createLabel('createdByEmail')}>
                  {createFieldDecorator('createdByEmail')(
                    <Input
                      type="email"
                      onChange={(e) => this.setItemValue('createdByEmail', e.target.value)}
                      disabled={item.status !== 'Open'}
                    />
                  )}
                </Form.Item>
              </Col>
            </Row>
            {['Closed', 'Declined'].includes(item.status) && (
              <Row>
                <Col md={16} xs={24}>
                  <Form.Item
                    label={item.status === 'Closed' ? t('convertedComments') : t('declinedComments')}
                  >
                    <Input.TextArea value={item.jobConversionComment} rows={5} disabled />
                  </Form.Item>
                </Col>
                <Col md={8} xs={24}>
                  <Form.Item label={item.status === 'Closed' ? t('convertedDate') : t('declinedDate')}>
                    <Input value={formatUserTime(item.jobConversionDate, item.jobConversionBy)} disabled />
                  </Form.Item>
                </Col>
              </Row>
            )}
            {item.status === 'Closed' && (
              <>
                <Row>
                  <Col md={16} xs={24}>
                    <Form.Item label={createLabel('jobName')}>
                      {createFieldDecorator('jobName')(<Input disabled />)}
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col md={16} xs={24}>
                    <Form.Item label={createLabel('jobAssetName')}>
                      {createFieldDecorator('jobAssetName')(<Input disabled />)}
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
            {this.props.enableAssetManagement && (
              <Tabs activeKey="documents">
                <Tabs.TabPane key="documents" tab={t('documents')} forceRender>
                  <Documents
                    domainObjectId={item.id}
                    domainObjectType="JobRequest"
                    items={this.state.documentItems ?? []}
                    pageTotals={this.state.documentItemsTotals ?? {}}
                    onChange={(values) => this.setState({ documentItems: values })}
                    readOnly={readOnly}
                  />
                </Tabs.TabPane>
              </Tabs>
            )}
          </div>
          <div className="form-buttons-container">
            {getUserPermission('AllowCreateJobRequestOnly') !== 'Yes' ? (
              <FormButtons
                readOnly={readOnly}
                onCancel={this.props.onCancel}
                onSubmit={this.handleSubmit}
                saveButtonLoading={this.state.saveButtonLoading}
                saveAndCloseButtonLoading={this.state.saveAndCloseButtonLoading}
              />
            ) : (
              <FormButtons
                readOnly={readOnly}
                cancelText={t('logout')}
                saveAndCloseText={t('saveAndNew')}
                saveAndCloseTooltip={t('saveAndNewDescription')}
                onCancel={this.props.onCancel}
                onSubmit={this.handleSubmit}
                saveButtonLoading={this.state.saveButtonLoading}
                saveAndCloseButtonLoading={this.state.saveAndCloseButtonLoading}
              />
            )}
          </div>
        </Form>
        <Modal
          title={t('createJob')}
          visible={this.state.createJobVisible}
          okText={t('create')}
          onOk={this.handleCreateJobOk}
          okButtonProps={{ disabled: !comment, loading: this.state.createJobLoading }}
          onCancel={() =>
            this.setState({
              createJobVisible: false,
              comment: '',
              jobTemplateId: 0,
              addJobRequestDocumentsToJob: false,
            })
          }
        >
          <Form.Item label={t('comments')} colon={false} required>
            <Input.TextArea
              rows={8}
              value={comment}
              onChange={(e) => this.setState({ comment: e.target.value })}
              autoFocus
            />
          </Form.Item>
          <Form.Item label={t('jobTemplate')} colon={false}>
            <Select
              onChange={(value) => this.setState({ jobTemplateId: value })}
              value={jobTemplateId}
              className="w-full"
            >
              <Option value={0}>{t('none')}</Option>
              {(this.state.templates ?? [])
                .filter((each) => each.id)
                .map((each) => (
                  <Option key={each.id} value={each.id}>
                    <span {...getOptionProps(each)}>{each.displayName}</span>
                  </Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Checkbox
              checked={addJobRequestDocumentsToJob}
              onChange={(e) => this.setState({ addJobRequestDocumentsToJob: e.target.checked })}
            >
              {t('addJobRequestDocumentsToJob')}
            </Checkbox>
          </Form.Item>
        </Modal>
        <Modal
          title={t('decline')}
          visible={this.state.declineVisible}
          okText={t('decline')}
          onOk={this.handleDeclineOk}
          okButtonProps={{ disabled: !comment, loading: this.state.declineLoading }}
          onCancel={() => this.setState({ declineVisible: false, comment: '' })}
        >
          <Form.Item label={t('comments')} colon={false} required>
            <Input.TextArea
              rows={8}
              value={comment}
              onChange={(e) => this.setState({ comment: e.target.value })}
              autoFocus
            />
          </Form.Item>
        </Modal>
      </>
    )
  }
}

export default Form.create({
  onValuesChange: (...params) => Emitter.emit(FORM_CHANGE_EVENT, ...params),
})(FormView)
